import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import Lottie from 'lottie-react'
import {
  CreditCard,
  TrendingUp,
  Shield,
  Smartphone,
  ArrowRight,
  Lock,
  BarChart3,
  Globe2,
  Zap,
  Users,
  DollarSign,
  CheckCircle,
  Star,
  Award,
  Clock,
  HeadphonesIcon,
  Wallet,
  PiggyBank,
  TrendingDown,
  Menu,
  X,
  ChevronDown,
  Play
} from 'lucide-react'
import GoogleTranslate from '../components/GoogleTranslate'

export default function LandingPage({ onShowAuth, onNavigate }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [activeTestimonial, setActiveTestimonial] = useState(0)
  const [welcomeAnimation, setWelcomeAnimation] = useState(null)
  const [securityAnimation, setSecurityAnimation] = useState(null)
  const [rocketAnimation, setRocketAnimation] = useState(null)

  useEffect(() => {
    fetch('/animations/welcome.json')
      .then(res => res.json())
      .then(data => setWelcomeAnimation(data))
      .catch(err => console.error('Error loading animation:', err))

    fetch('/animations/security-employee.json')
      .then(res => res.json())
      .then(data => setSecurityAnimation(data))
      .catch(err => console.error('Error loading animation:', err))

    fetch('/animations/rocket.json')
      .then(res => res.json())
      .then(data => setRocketAnimation(data))
      .catch(err => console.error('Error loading animation:', err))
  }, [])

  const features = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Military-Grade Security",
      description: "256-bit encryption, biometric authentication, and real-time fraud detection protect your assets 24/7",
      gradient: "from-blue-500 to-blue-600"
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Mobile-First Banking",
      description: "Manage everything from your phone. Deposit checks, transfer funds, and track spending instantly",
      gradient: "from-purple-500 to-purple-600"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Smart Investments",
      description: "AI-powered portfolio management with real-time analytics and personalized recommendations",
      gradient: "from-emerald-500 to-emerald-600"
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Premium Cards",
      description: "Contactless payments, cashback rewards, and virtual cards for secure online shopping",
      gradient: "from-orange-500 to-orange-600"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Instant Transfers",
      description: "Send and receive money in seconds with zero fees. Works with all major payment networks",
      gradient: "from-cyan-500 to-cyan-600"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Financial Insights",
      description: "Advanced analytics dashboard showing spending patterns, budgets, and saving opportunities",
      gradient: "from-pink-500 to-pink-600"
    },
    {
      icon: <Wallet className="w-8 h-8" />,
      title: "Digital Wallet",
      description: "Store cards, tickets, and passes in one secure place. Compatible with Apple Pay & Google Pay",
      gradient: "from-indigo-500 to-indigo-600"
    },
    {
      icon: <PiggyBank className="w-8 h-8" />,
      title: "Auto-Savings",
      description: "Round up purchases and automatically save the difference. Reach your goals faster",
      gradient: "from-teal-500 to-teal-600"
    }
  ]

  const stats = [
    { value: '2.5M+', label: 'Active Users', icon: <Users className="w-6 h-6" /> },
    { value: '$15B+', label: 'Assets Managed', icon: <DollarSign className="w-6 h-6" /> },
    { value: '99.99%', label: 'Uptime', icon: <Zap className="w-6 h-6" /> },
    { value: '150+', label: 'Countries', icon: <Globe2 className="w-6 h-6" /> }
  ]

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Entrepreneur",
      image: "👩‍💼",
      rating: 5,
      text: "Swift Gold Fiduciary transformed how I manage my business finances. The analytics dashboard is incredible, and their support team is always there when I need them."
    },
    {
      name: "Michael Chen",
      role: "Software Engineer",
      image: "👨‍💻",
      rating: 5,
      text: "Best banking app I've ever used. The mobile experience is flawless, and I love the investment tools. Switched from my old bank and never looked back."
    },
    {
      name: "Emily Rodriguez",
      role: "Medical Professional",
      image: "👩‍⚕️",
      rating: 5,
      text: "Security was my top concern, and Swift Gold Fiduciary exceeded all expectations. The biometric login and real-time alerts give me complete peace of mind."
    }
  ]

  const pricingPlans = [
    {
      name: "Essential",
      price: "Free",
      description: "Perfect for getting started",
      features: [
        "Free checking account",
        "Debit card with cashback",
        "Mobile app access",
        "24/7 customer support",
        "ATM fee reimbursement up to $10/month"
      ],
      highlighted: false
    },
    {
      name: "Premium",
      price: "$9.99",
      period: "/month",
      description: "For serious savers and investors",
      features: [
        "Everything in Essential",
        "4.5% APY savings account",
        "No foreign transaction fees",
        "Investment portfolio management",
        "Priority customer support",
        "Unlimited ATM fee reimbursement",
        "Free wire transfers"
      ],
      highlighted: true
    },
    {
      name: "Business",
      price: "$29.99",
      period: "/month",
      description: "Built for growing businesses",
      features: [
        "Everything in Premium",
        "Multiple user accounts",
        "Invoicing & payment processing",
        "Business credit card",
        "Expense tracking & reporting",
        "Dedicated account manager",
        "API access"
      ],
      highlighted: false
    }
  ]

  const faqs = [
    {
      question: "Is my money FDIC insured?",
      answer: "Yes, all deposits are FDIC insured up to $250,000 per depositor, per account category."
    },
    {
      question: "Are there any monthly fees?",
      answer: "Our Essential plan is completely free. Premium and Business plans have monthly fees but offer additional benefits that far outweigh the cost."
    },
    {
      question: "How do I deposit money?",
      answer: "You can deposit money via direct deposit, mobile check deposit, wire transfer, or ACH transfer from another bank. All methods are secure and fast."
    },
    {
      question: "Can I use Swift Gold Fiduciary internationally?",
      answer: "Absolutely! Use your Swift Gold Fiduciary card anywhere Visa is accepted worldwide. Premium members enjoy zero foreign transaction fees."
    },
    {
      question: "How secure is Swift Gold Fiduciary?",
      answer: "We use bank-level 256-bit encryption, multi-factor authentication, biometric login, and 24/7 fraud monitoring to keep your money safe."
    },
    {
      question: "How long does account opening take?",
      answer: "You can open an account in under 5 minutes right from your phone. No paperwork, no branch visits required."
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-lg border-b border-slate-200 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-lg md:rounded-xl flex items-center justify-center shadow-lg shadow-amber-200/50">
                <DollarSign className="w-6 h-6 md:w-7 md:h-7 text-white" />
              </div>
              <div>
                <span className="text-xl md:text-2xl font-bold text-slate-900">Swift Gold Fiduciary</span>
                <p className="text-xs text-amber-600 hidden sm:block font-medium">FDIC Insured</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              <a href="#features" className="text-slate-700 hover:text-amber-600 font-medium transition-colors">Features</a>
              <a href="#pricing" className="text-slate-700 hover:text-amber-600 font-medium transition-colors">Pricing</a>
              <a href="#security" className="text-slate-700 hover:text-amber-600 font-medium transition-colors">Security</a>
              <a href="#testimonials" className="text-slate-700 hover:text-amber-600 font-medium transition-colors">Reviews</a>
              <a href="#faq" className="text-slate-700 hover:text-amber-600 font-medium transition-colors">FAQ</a>
              <GoogleTranslate variant="minimal" />
              <button
                onClick={onShowAuth}
                className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-lg font-semibold transition-all hover:shadow-lg hover:shadow-amber-200/50"
              >
                Sign In
              </button>
              <button
                onClick={onShowAuth}
                className="px-6 py-2.5 border-2 border-amber-500 text-amber-600 hover:bg-amber-50 rounded-lg font-semibold transition-all"
              >
                Open Account
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 text-slate-600 hover:text-slate-900"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:hidden py-4 border-t border-slate-200"
            >
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-slate-700 hover:text-amber-600 font-medium">Features</a>
                <a href="#pricing" className="text-slate-700 hover:text-amber-600 font-medium">Pricing</a>
                <a href="#security" className="text-slate-700 hover:text-amber-600 font-medium">Security</a>
                <a href="#testimonials" className="text-slate-700 hover:text-amber-600 font-medium">Reviews</a>
                <a href="#faq" className="text-slate-700 hover:text-amber-600 font-medium">FAQ</a>
                <div className="py-2">
                  <GoogleTranslate variant="minimal" />
                </div>
                <button onClick={onShowAuth} className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-semibold">
                  Sign In
                </button>
                <button onClick={onShowAuth} className="w-full py-2.5 border-2 border-amber-500 text-amber-600 rounded-lg font-semibold">
                  Open Account
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Hero Section with Animation */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-b from-amber-50/50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold">
                  Now serving 2.5M+ customers worldwide
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-slate-900 mb-6 leading-tight">
                Banking Made
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-amber-600">
                  Simple & Secure
                </span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-xl">
                Join millions who trust Swift Gold Fiduciary for modern banking. Open an account in minutes,
                get premium benefits, and experience the future of finance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button
                  onClick={onShowAuth}
                  className="group px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-bold text-lg transition-all hover:shadow-2xl hover:shadow-amber-500/30 flex items-center justify-center gap-3"
                >
                  Open Free Account
                  <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </button>
                <button className="group px-8 py-4 border-2 border-slate-300 hover:border-amber-500 text-slate-700 hover:text-amber-600 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-3">
                  <Play className="w-6 h-6" />
                  Learn More
                </button>
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-500" />
                  <span>No monthly fees</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-500" />
                  <span>FDIC insured</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-amber-500" />
                  <span>5 min signup</span>
                </div>
              </div>
            </motion.div>

            {/* Hero Image - Luxury Banking */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&q=80"
                  alt="Luxury Banking"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-white font-medium text-lg">Primary Account</span>
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="mb-4">
                      <p className="text-white/70 text-sm mb-1">Available Balance</p>
                      <p className="text-4xl font-bold text-white">$48,592.00</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
                      <div>
                        <p className="text-white/60 text-xs mb-1">Account Number</p>
                        <p className="text-white font-mono text-sm">**** **** 4892</p>
                      </div>
                      <div>
                        <p className="text-white/60 text-xs mb-1">Valid Thru</p>
                        <p className="text-white font-mono text-sm">12/26</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl md:rounded-2xl mb-3 md:mb-4 shadow-lg shadow-amber-500/20">
                  {stat.icon}
                </div>
                <p className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-2">{stat.value}</p>
                <p className="text-xs sm:text-sm md:text-base text-slate-400 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
              Powerful features designed to help you save more, spend smarter, and achieve your financial goals
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="group bg-white p-8 rounded-2xl border-2 border-slate-100 hover:border-amber-400 hover:shadow-xl hover:shadow-amber-100/50 transition-all cursor-pointer"
              >
                <div className={`inline-flex w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section with Image */}
      <section id="security" className="py-24 bg-gradient-to-b from-white to-amber-50/30 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800&q=80"
                  alt="Security"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-3 bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">256-bit Encryption</p>
                      <p className="text-white/70 text-sm">Military-grade protection</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-block mb-4">
                <span className="px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold">
                  Bank-Level Security
                </span>
              </div>
              <h2 className="text-5xl font-extrabold text-slate-900 mb-6">
                Your Money, Safer Than Ever
              </h2>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                We take security seriously. Every transaction is protected with military-grade encryption,
                and our team monitors for suspicious activity 24/7/365.
              </p>

              <div className="space-y-6">
                {[
                  {
                    title: "256-bit Encryption",
                    description: "The same level of security used by the military and government agencies"
                  },
                  {
                    title: "Biometric Authentication",
                    description: "Face ID, Touch ID, and fingerprint login for maximum security"
                  },
                  {
                    title: "Real-Time Fraud Detection",
                    description: "AI monitors every transaction and alerts you instantly of suspicious activity"
                  },
                  {
                    title: "Zero Liability Protection",
                    description: "You're never responsible for unauthorized transactions"
                  }
                ].map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-amber-600" />
                      </div>
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-900 mb-1">{item.title}</h4>
                      <p className="text-slate-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-white px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-slate-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Choose the plan that fits your needs. Upgrade, downgrade, or cancel anytime.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative bg-white rounded-2xl p-8 ${
                  plan.highlighted
                    ? 'border-4 border-amber-400 shadow-2xl shadow-amber-100/50 transform scale-105'
                    : 'border-2 border-slate-200'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-5 left-0 right-0 flex justify-center">
                    <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <p className="text-slate-600 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-extrabold text-slate-900">{plan.price}</span>
                    {plan.period && <span className="text-slate-600">{plan.period}</span>}
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onShowAuth}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-lg hover:shadow-xl hover:shadow-amber-200/50'
                      : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                  }`}
                >
                  Get Started
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 bg-gradient-to-b from-amber-50/30 to-white px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-slate-900 mb-4">
              Loved by Millions Worldwide
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Don't just take our word for it. Here's what our customers have to say.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl border border-slate-100 hover:shadow-xl hover:shadow-amber-100/30 transition-all hover:border-amber-200"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-4">
                  <div className="text-5xl">{testimonial.image}</div>
                  <div>
                    <p className="font-bold text-slate-900">{testimonial.name}</p>
                    <p className="text-slate-600 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl font-extrabold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-600">
              Everything you need to know about Swift Gold Fiduciary
            </p>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="bg-white p-6 rounded-xl border border-slate-100 hover:shadow-lg hover:border-amber-200 transition-all"
              >
                <h3 className="text-lg font-bold text-slate-900 mb-2">{faq.question}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Luxury Image */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=1600&q=80"
            alt="Luxury Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/80"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-5xl md:text-6xl font-extrabold text-white mb-6">
                Ready to Launch Your{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">Financial Future?</span>
              </h2>
              <p className="text-2xl text-slate-300 mb-8">
                Join 2.5 million users who've already made the switch. Open your free account in just 5 minutes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={onShowAuth}
                  className="px-10 py-5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl font-bold text-xl transition-all hover:shadow-2xl hover:shadow-amber-500/30 inline-flex items-center justify-center gap-3"
                >
                  Open Free Account
                  <ArrowRight className="w-6 h-6" />
                </button>
                <button className="px-10 py-5 border-2 border-white/30 text-white hover:bg-white/10 hover:border-amber-400 rounded-xl font-bold text-xl transition-all">
                  Contact Us
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&q=80"
                  alt="Financial Success"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-4 bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/20">
                    <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center">
                      <DollarSign className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">Premium Banking</p>
                      <p className="text-white/70">Experience luxury financial services</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
                  <DollarSign className="w-7 h-7 text-white" />
                </div>
                <div>
                  <span className="text-2xl font-bold text-white">Swift Gold Fiduciary</span>
                  <p className="text-xs text-amber-400">FDIC Insured</p>
                </div>
              </div>
              <p className="text-slate-400 mb-4 leading-relaxed">
                Modern banking for modern life. Secure, simple, and always available.
                Member FDIC. Equal Housing Lender.
              </p>
              <div className="flex gap-4">
                {['Twitter', 'Facebook', 'LinkedIn', 'Instagram'].map((social) => (
                  <a key={social} href="#" className="w-10 h-10 bg-slate-800 hover:bg-amber-500/20 hover:text-amber-400 rounded-lg flex items-center justify-center transition-colors">
                    <span className="text-xs font-bold">{social[0]}</span>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4">Products</h3>
              <ul className="space-y-3 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Checking</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Savings</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Credit Cards</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Loans</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Investments</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4">Company</h3>
              <ul className="space-y-3 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Careers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Press</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-white font-bold mb-4">Support</h3>
              <ul className="space-y-3 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Accessibility</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">&copy; 2024 Swift Gold Fiduciary. All rights reserved.</p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Cookies</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
